import React, { useState, ComponentType, MutableRefObject, ReactNode, ForwardedRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

interface WithFormProps {
  dirtyFlagEnable?: boolean;
  ref?: MutableRefObject<HTMLElement>;
  children?: ReactNode; // Move children prop here
}

type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
type Diff<T, K> = Omit<T, keyof K>;

const message = 'Are you sure you want to close this window ?';

const withForm = <P extends object>(WrappedComponent: ComponentType<P>) => {
  type HocProps = Diff<P, WithFormProps> & {
    forwardedRef?: ForwardedRef<HTMLElement>;
    dirtyFlagEnable?: boolean;
    children?: ReactNode;
  };

  function Hoc(props: HocProps) {
    const { forwardedRef, children, ...rest } = props;
    const dirtyCheckEnable = props.dirtyFlagEnable ?? true;
    const [guardIsActive, setGuardIsActive] = useState(false);

    const onBeforeUnload = (ev: any) => {
      ev.returnValue = message;
      return message;
    };

    // For browser closing/reload handle with beforeunload event
    const handleGuardSetActive = (active: boolean, callbackFn: any) => {
      // if no change made, then just return
      if (guardIsActive === active) {
        if (typeof callbackFn === 'function') {
          callbackFn();
        }
        return;
      }
      // add/remove event listeners
      if (active) {
        window.addEventListener('beforeunload', onBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', onBeforeUnload);
      }

      if (guardIsActive && typeof callbackFn === 'function') {
        // Use a MUI Dialog instead of Modal
        const handleClose = () => {
          callbackFn();
          // Confirmed for close modal/drawer
          setGuardIsActive(false);
        };

        return (
          <Dialog open={true} onClose={handleClose}>
            <DialogTitle>{message}</DialogTitle>
            <DialogContent>{children}</DialogContent>
            <DialogActions>
              <Button onClick={handleClose} variant='contained' color='primary'>
                Confirm
              </Button>
              <Button onClick={() => setGuardIsActive(true)} variant='outlined' color='primary'>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        );
      }
      setGuardIsActive(active);
    };

    return dirtyCheckEnable ? (
      <WrappedComponent {...(rest as P)} ref={forwardedRef} dirtySetActive={handleGuardSetActive}>
        {children}
      </WrappedComponent>
    ) : (
      <WrappedComponent {...(rest as P)} ref={forwardedRef}>
        {children}
      </WrappedComponent>
    );
  }

  // ForwardRef
  return React.forwardRef<HTMLElement, HocProps>((props, ref) => {
    return <Hoc {...(props as HocProps)} forwardedRef={ref} />;
  });
};

export default withForm;
